import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { Page, ResponseError } from '../type/type';
import { NavigateFunction } from 'react-router-dom';
import { useFranchiseStore } from './useFranchise';

// 상담 주문 상태 타입
export interface IConsultOrderStatus {
  code: string;
  description: string;
}

// 상담 주문 고객 정보 타입
export interface IConsultOrderCustInfo {
  name: string;
  solarType: string;
  year: number;
  month: number;
  day: number;
  hour: number | null;
  minute: number | null;
  sexType: string;
}

// 사주 결과 타입 (백엔드에서 동적 Map으로 반환)
export type ISajuResult = Record<string, any>;

// 상담 주문 타입
export interface IConsultOrder {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  oder: number;
  status: IConsultOrderStatus;
  confirmDt: string | null;
  name: string;
  solarType: string;
  birthday: string;
  sexType: string;
  year: number;
  month: number;
  day: number;
  hour: number | null;
  minute: number | null;
  franchiseName: string | null;
}

// 상담 주문 상세 응답 타입
export interface IConsultOrderDetail {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  oder: number;
  status: IConsultOrderStatus;
  confirmDt: string | null;
  custInfo: IConsultOrderCustInfo;
  sajuResult: ISajuResult | null;
}

// 상담 주문 등록 요청 타입
export interface IConsultOrderSave {
  sequence: string;
  name: string;
  solarType: string;
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  sex: string;
}

// 상담 주문 목록 응답 타입
interface ConsultOrderResponse extends Page {
  content: IConsultOrder[];
}

// 주문 확정 결과 타입
interface ConfirmResult {
  confirmedCount: number;
  reducedAmount: number;
}

// Zustand Store Data 타입
type ConsultOrderData = {
  getOrderLoading: boolean;
  postOrderLoading: boolean;
  deleteOrderLoading: boolean;
  confirmOrderLoading: boolean;

  orders: IConsultOrder[];
  ordersPage: Page | null;
  orderDetail: IConsultOrderDetail | null;
  orderDetailError: string | null;
  statuses: IConsultOrderStatus[];
};

// Zustand Store Action 타입
type ConsultOrderAction = {
  getOrders: (params: {
    status?: string;
    startDt?: string;
    endDt?: string;
    name?: string;
    page?: number;
    size?: number;
  }) => void;

  getOrderDetail: (id: string) => void;

  postOrder: (
    orderData: IConsultOrderSave[],
    navigate?: NavigateFunction
  ) => Promise<void>;

  confirmOrders: (ids: string[], navigate?: NavigateFunction) => Promise<void>;

  deleteOrder: (id: string, navigate: NavigateFunction) => void;

  deleteOrders: (ids: string[]) => void;

  getStatuses: () => void;
};

export const useConsultOrderStore = create<ConsultOrderData & ConsultOrderAction>(
  (set, get) => ({
    getOrderLoading: false,
    postOrderLoading: false,
    deleteOrderLoading: false,
    confirmOrderLoading: false,

    orders: [],
    ordersPage: null,
    orderDetail: null,
    orderDetailError: null,
    statuses: [],

    // 상담 주문 목록 가져오기
    getOrders: async ({ status, startDt, endDt, name, page, size }) => {
      set({ getOrderLoading: true });
      set({ orderDetail: null });

      try {
        const response = await apiClient.get<ConsultOrderResponse>(
          '/consult-order',
          {
            params: {
              status,
              startDt,
              endDt,
              name,
              page,
              size,
            },
          }
        );

        const { content, ...rest } = response.data;
        set({ orders: content });
        set({ ordersPage: rest });
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('상담 주문 목록을 가져오지 못했습니다.');
      }

      set({ getOrderLoading: false });
    },

    // 상담 주문 상세 가져오기
    getOrderDetail: async id => {
      set({ getOrderLoading: true, orderDetail: null, orderDetailError: null });
      try {
        const response = await apiClient.get<IConsultOrderDetail>(
          `/consult-order/${id}`
        );
        set({ orderDetail: response.data });
      } catch (error) {
        console.error('API 요청 실패:', error);
        let errorMsg = '상담 주문 상세를 가져오지 못했습니다.';
        if (isAxiosError<ResponseError>(error)) {
          errorMsg = error.response?.data.errorMsg || errorMsg;
        }
        set({ orderDetailError: errorMsg });
        toast.error(errorMsg);
      } finally {
        set({ getOrderLoading: false });
      }
    },

    // 상담 주문 등록
    postOrder: async (orderData, navigate) => {
      set({ orders: [], ordersPage: null });
      set({ postOrderLoading: true });

      try {
        const response = await apiClient.post('/consult-order', orderData);
        if (response.status === 200) {
          toast.success('상담 주문이 성공적으로 등록되었습니다!');
          if (navigate) {
            navigate(`/consult-order/list?_t=${Date.now()}`);
          }
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('상담 주문 등록 실패:', error);
          toast.error('상담 주문 등록에 실패했습니다.');
        }
      }

      set({ postOrderLoading: false });
    },

    // 상담 주문 확정
    confirmOrders: async (ids, navigate) => {
      set({ orders: [] });
      set({ confirmOrderLoading: true });

      try {
        const response = await apiClient.post<ConfirmResult>(
          '/consult-order/confirm',
          {
            idList: ids,
          }
        );
        if (response.status === 200) {
          const result = response.data;
          toast.success(
            `${result.confirmedCount}건 주문이 확정되었습니다. (${result.reducedAmount}원 차감)`
          );
          if (navigate) {
            navigate('/consult-order/list');
          } else {
            await get().getOrders({ status: 'WAIT' });
            const franchiseStore = useFranchiseStore.getState();
            await franchiseStore.getMyFranchise();
          }
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('상담 주문 확정 실패:', error);
          toast.error('상담 주문 확정에 실패했습니다.');
        }
        await get().getOrders({ status: 'WAIT' });
      }

      set({ confirmOrderLoading: false });
    },

    // 상담 주문 삭제
    deleteOrder: async (id, navigate) => {
      set({ orders: [] });
      set({ deleteOrderLoading: true });

      try {
        const response = await apiClient.delete(`/consult-order/${id}`);
        if (response.status === 200) {
          toast.success('상담 주문이 성공적으로 삭제되었습니다!');
          navigate('/consult-order/list');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('상담 주문 삭제 실패:', error);
          toast.error('상담 주문 삭제에 실패했습니다.');
        }
      }

      set({ deleteOrderLoading: false });
    },

    // 여러 상담 주문 삭제
    deleteOrders: async ids => {
      set({ orders: [] });
      set({ deleteOrderLoading: true });

      try {
        const response = await apiClient.delete('/consult-order', {
          data: {
            idList: ids,
          },
        });
        if (response.status === 200) {
          toast.success('선택한 상담 주문이 성공적으로 삭제되었습니다!');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('상담 주문 삭제 실패:', error);
          toast.error('상담 주문 삭제에 실패했습니다.');
        }
      }

      set({ deleteOrderLoading: false });
    },

    // 상태 목록 가져오기
    getStatuses: async () => {
      const statuses = get().statuses;
      if (statuses.length > 0) return;

      try {
        const response = await apiClient.get<IConsultOrderStatus[]>(
          '/consult-order/status'
        );
        set({ statuses: response.data });
      } catch (error) {
        console.log(error);
      }
    },
  })
);
